import dayjs from 'dayjs';

/**
 * Nombre del PDF de una cita. Es un CONTRATO con el backend: el front sube el archivo con este nombre
 * y el aviso de WhatsApp lo referencia con el que calcula CitaNotificaciones::nombreArchivo() en PHP.
 * Si difieren en una sola letra, el paciente recibe un adjunto roto (404). Los dos lados se verifican
 * contra la misma tabla de referencia: backend/tests/fixtures/nombres_pdf.json.
 */
export function limpiarTexto(texto) {
  return texto
    .normalize('NFD')                    // separa letras y acentos
    .replace(/[̀-ͯ]/g, '')     // elimina los acentos
    .replace(/\s+/g, '_')                // espacios por guión bajo
    .replace(/[^a-zA-Z0-9_]/g, '');      // quita lo que no sea letra, número o _
}

/** `fecha` llega ya formateada: dayjs(dia).format('dddd, D [de] MMMM [de] YYYY') con el idioma en español. */
export function nombreArchivoCita(idTurno, nombre, fecha) {
  return `Cita_${idTurno}_${limpiarTexto(nombre)}_${limpiarTexto(fecha)}.pdf`;
}

/**
 * Cancelar OBLIGA a dejar una nota: el servidor la guarda en Comentarios (la línea de tiempo de la cita) y rechaza la
 * cancelación sin ella. Devuelve el mensaje de error a mostrar, o undefined si la nota sirve.
 */
export function validarNotaCancelacion(nota) {
  const n = (nota || '').trim();
  if (!n) return 'Debes escribir una nota';
  if (n.length > 200) return 'La nota admite máximo 200 caracteres';
  return undefined;
}

/** Pide la nota de cancelación con SweetAlert. Devuelve el texto, o null si la persona se arrepiente y vuelve. */
export async function pedirNotaCancelacion(Swal) {
  const { value } = await Swal.fire({
    title: 'Nota de cancelación',
    text: 'Escribe por qué se cancela. Quedará en la línea de tiempo (Comentarios) de la cita.',
    input: 'textarea',
    inputAttributes: { maxlength: 200, 'aria-label': 'Nota de cancelación' },
    showCancelButton: true,
    confirmButtonText: 'Cancelar la cita',
    cancelButtonText: 'Volver',
    inputValidator: validarNotaCancelacion,
  });
  return value ? value.trim() : null;
}

/**
 * No se deja cancelar ni dar de alta una cita si al paciente le falta la encuesta sociodemográfica (ver
 * Paciente::encuestaCompleta en el backend, que es quien de verdad lo impide con un 422). Esto solo evita que la
 * persona escriba una nota de cancelación que de todos modos se va a rechazar — la fuente de verdad es el servidor,
 * por eso se apoya en `datosPacienteCompletados`, el mismo flag que ya trae cada turno.
 */
export function pacienteConEncuestaIncompleta(turno) {
  return !!turno?.idPaciente && !turno.datosPacienteCompletados;
}

/**
 * Aviso cuando falta la encuesta. Devuelve true si bloqueó (la persona no debe seguir con la acción).
 * `avisoFalta` es la función de src/utils/swal.js (inyectada, no importada, para que el test no dependa
 * de SweetAlert2 real — mismo patrón que `pedirNotaCancelacion`).
 */
export async function avisoEncuestaIncompleta(turno, avisoFalta) {
  if (!pacienteConEncuestaIncompleta(turno)) return false;
  await avisoFalta(
    'Antes de cancelar o dar de alta esta cita hay que completar los datos sociodemográficos del paciente (pestaña Datos).'
  );
  return true;
}

/**
 * Mensaje de éxito al guardar un comentario, según el botón que lo disparó (ver
 * docs/qa-cowork/propuesta-avisos-unificados.md). `tipo` es el mismo código que ya viaja al backend
 * (1=solo comentario, 2=dar de alta, 3/4=agendar N citas futuras).
 */
export function mensajeComentarioGuardado(tipo, nCitasAgendadas) {
  if (tipo === 2) return 'Paciente dado de alta';
  if (tipo === 3 || tipo === 4) {
    const n = nCitasAgendadas ?? 0;
    return `Comentario guardado y ${n} cita${n === 1 ? '' : 's'} agendada${n === 1 ? '' : 's'}`;
  }
  return 'Comentario guardado';
}

/**
 * Mensaje tras reprogramar una cita. El backend solo confirma el cambio ('Cita reprogramada'); la fecha
 * y hora nuevas ya las tiene el front (son las que mandó en el payload), así que se arman aquí.
 */
export function mensajeReprogramada(nuevaFecha, nuevaHoraInicio) {
  return `Cita reprogramada para el ${dayjs(nuevaFecha).format('DD/MM')} a las ${nuevaHoraInicio}`;
}

/**
 * Mensaje tras cancelar en lote, a partir de los conteos que ya devuelve el backend
 * (`canceladas`/`bloqueadas`), en vez del texto fijo que mandaba antes.
 */
export function mensajeCancelarLote(canceladas, bloqueadas) {
  if (!bloqueadas) {
    return `${canceladas} cita${canceladas === 1 ? '' : 's'} cancelada${canceladas === 1 ? '' : 's'}`;
  }
  if (!canceladas) {
    return 'Ninguna cita se pudo cancelar: faltan los datos del paciente (pestaña Datos).';
  }
  return `Se cancelaron ${canceladas}. ${bloqueadas} no se pudieron: faltan los datos del paciente (pestaña Datos).`;
}

/**
 * Fecha y hora de una entrada de la línea de tiempo (`comentarios.fecha` es DATETIME, hora de México). Se formatea con una
 * expresión regular y no con `new Date()`, que interpretaría la cadena con la zona del navegador. Las notas anteriores al
 * cambio solo tenían fecha (quedaron a las 00:00:00): esas se muestran sin hora, como antes.
 */
export function formatoFechaComentario(fecha) {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(fecha || '');
  if (!m) return fecha || '';
  const dia = `${m[3]}/${m[2]}/${m[1]}`;
  const hora = m[4] ? `${m[4]}:${m[5]}` : '';
  return hora && hora !== '00:00' ? `${dia} ${hora}` : dia;
}
